import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Navbar";
import { DiscordIcon, startDiscordLogin } from "@/components/discord";
import { Button } from "@/components/ui/button";
import { Crown, UserRound, Check, Loader2, ArrowLeft } from "lucide-react";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [role, setRole] = useState(params.get("role") === "member" ? "member" : "creator");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(user.role === "creator" ? "/creator" : "/member", { replace: true });
  }, [user, navigate]);

  const connect = async () => {
    setBusy(true);
    try {
      await startDiscordLogin(role, api);
    } catch (e) {
      toast.error("Could not start Discord login. Try again.");
      setBusy(false);
    }
  };

  const roles = [
    { key: "creator", label: "Creator Storefront", icon: Crown, desc: "Sell memberships & digital products",
      perks: ["Your own link-in-bio storefront", "First 10 subscribers free", "Sell downloadable products"] },
    { key: "member", label: "Subscriber Portal", icon: UserRound, desc: "Support & unlock your creators",
      perks: ["Subscribe to your favourite creators", "Manage billing anytime", "Instant digital downloads"] },
  ];
  const active = roles.find((r) => r.key === role);

  return (
    <div className="min-h-screen bg-[#FAF9F6] grain">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Logo />
        <button onClick={() => navigate("/")} className="flex items-center gap-1 text-sm font-semibold text-[#111114] hover:text-violet-600" data-testid="auth-back-home">
          <ArrowLeft className="w-4 h-4" /> Home
        </button>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 lg:py-16 grid lg:grid-cols-2 gap-8 items-center">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <span className="chip bg-lime-200 inline-block mb-5">Discord-powered</span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[#111114] leading-[1.05] mb-4">
            One tap in.<br />No passwords, ever.
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mb-6 max-w-md">
            VangTech Subs uses your Discord account to sign you in securely. Pick how you want to start.
          </p>
          <div className="hidden lg:block card-pop p-6 bg-white max-w-sm">
            <p className="eyebrow text-violet-600 mb-3">You'll get</p>
            <ul className="space-y-2.5">
              {active.perks.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm font-medium text-[#111114]">
                  <span className="w-5 h-5 rounded-md bg-lime-300 border-2 border-[#111114] flex items-center justify-center shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#111114]" strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }}
          className="card-pop bg-white p-6 sm:p-8">
          <p className="eyebrow text-slate-500 mb-3">Choose your mode</p>
          <div className="grid grid-cols-1 gap-3 mb-6">
            {roles.map((r) => {
              const on = role === r.key;
              return (
                <button key={r.key} onClick={() => setRole(r.key)} data-testid={`role-${r.key}`}
                  className={`text-left rounded-xl border-2 border-[#111114] p-4 flex items-center gap-4 transition-all ${on ? "bg-violet-600 text-white" : "bg-white text-[#111114] hover:bg-violet-50"}`}>
                  <div className={`w-11 h-11 rounded-lg border-2 border-[#111114] flex items-center justify-center shrink-0 ${on ? "bg-lime-300" : "bg-violet-100"}`}>
                    <r.icon className="w-5 h-5 text-[#111114]" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display font-bold">{r.label}</p>
                    <p className={`text-xs ${on ? "text-violet-100" : "text-slate-500"}`}>{r.desc}</p>
                  </div>
                  <div className={`ml-auto w-5 h-5 rounded-full border-2 border-[#111114] shrink-0 ${on ? "bg-lime-300" : "bg-white"}`} />
                </button>
              );
            })}
          </div>

          <Button onClick={connect} disabled={busy || loading}
            className="w-full bg-[#5865F2] hover:bg-[#4752c4] text-white btn-pop rounded-xl h-13 text-base font-bold"
            data-testid="discord-login-btn">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (
              <><DiscordIcon className="w-5 h-5 mr-2" /> Continue with Discord</>
            )}
          </Button>
          <p className="text-center text-xs text-slate-500 mt-4">
            Continuing as <span className="text-violet-600 font-bold capitalize">{role}</span>. We only read your name, avatar & email.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
